import * as React from 'react';
import { PanelContext } from '../../components_v2/panel/context';
import { TagType } from '../../components_v2/toolbarFilter/tags/Model';
import { translateToString } from '../../styles/global/translate';
import { useEditFormsPermission } from '../../utils/customHooks';
import SecurityGroupList from '../authorization/groups/GroupList';
import Members from '../authorization/users/Members';
import Checkouts from '../checkouts/Checkouts';
import { Exports } from '../exports/Exports';
import FormTemplateList from '../forms/FormTemplateList';
import FormViewsList from '../forms/FormViews';
import MetadataCreator from '../forms/MetadataCreator';
import { ToolbarState } from '../globals/mainPage/mainPage';
import Import from '../import/Import';
import Teams from '../teams/Teams';
import SettingsInterpreter, { SystemCategory } from './settingsInterpreter';
import ApiConsumption from './subSettings/apiConsumption';
import BrandManagement from './subSettings/brandManagement';
import CategoryManagement from './subSettings/categoryManagement';
import EventFrequencyRules from './subSettings/eventFrequencyRules';
import EmailsV2Settings from './subSettings/emailsV2Settings';
import EmailTemplateSettings from './subSettings/emailTemplateSettings';
import MappingFieldsSettings from './subSettings/mappingSettings';
import MultidimensionalAssortmentsManagement from './subSettings/multidimensionalAssortmentsManagement';
import DriveRestrictionsSettings from './subSettings/driveRestrictions';
import OrderNotificationsSettings from './subSettings/orderNotificationsSettings';
import StatusAndTagsSettings from './subSettings/statusesAndTagsSettings';
import PromotionFieldsSettings from './subSettings/PromotionFieldsSettings';
import ThirdPartyIntegration from './subSettings/thirdPartyIntegration';
import MyProfile from './userSettings/myProfile';
import SecuritySettings from './userSettings/security';
import UserSubSettings from './userSettings/userSubSettings';

export default function SystemSettings(props: {
	setToolBarState: (value: ToolbarState) => void;
}) {
	const isAllowedToEditForms = useEditFormsPermission();
	const { setDisabled } = React.useContext(PanelContext);

	React.useEffect(() => {
		setDisabled(true);
		return () => {
			setDisabled(false);
		};
	}, []);

	const setToolBarState = React.useCallback(
		(state: ToolbarState) =>
			props.setToolBarState({
				bottomLeftToolbarComponent: <></>,
				bottomRightToolbarComponent: <></>,
				...state,
				title: translateToString('settings')
			}),
		[]
	);
	const categories: SystemCategory[] = [
		{
			title: translateToString('user_settings'),
			children: [
				{
					name: translateToString('your_preferences'),
					children: [
						{
							key: 'my_profile',
							component: <MyProfile setToolBarState={setToolBarState} />,
							restricted: { objectAction: 'ViewUserSettings' }
						},
						{
							key: 'my_settings',
							component: <UserSubSettings setToolBarState={setToolBarState} />
						},
						{
							key: 'security',
							component: <SecuritySettings setToolBarState={setToolBarState} />
						}
						// { key:'security', component: <GroupList setToolBarState={setToolBarState} />},
					]
				},
				{
					name: 'Api',
					restricted: { objectAction: 'ViewApiSettings' },
					children: [
						{
							key: 'api_consumption',
							component: <ApiConsumption setToolBarState={setToolBarState} />,
						}
					]
				}
			]
		},
		{
			title: translateToString('system_settings'),
			children: [
				{
					name: translateToString('account_settings'),
					restricted: { objectAction: 'ViewAccountSettings' },
					owner: true,
					children: [
						// { key:'company_informations', component: <CompanyInformationsSettings />},
						{
							key: 'users',
							component: <Members setToolBarState={setToolBarState} />
						},
						{
							key: 'groups',
							component: <SecurityGroupList setToolBarState={setToolBarState} />
						},
						{
							key: 'teams',
							component: <Teams setToolBarState={setToolBarState} />
						},
						{
							key: 'drive_restrictions',
							component: <DriveRestrictionsSettings setToolBarState={setToolBarState} />
						}
					]
				},
				{
					name: translateToString('general.field_settings'),
					restricted: { objectAction: 'ViewCompanySettings' },
					children: [
						{
							key: 'company_fields',
							component: (
								<StatusAndTagsSettings
									setToolBarState={setToolBarState}
									type={TagType.COMPANY}
								/>
							)
						},
						{
							key: 'contacts_fields',
							component: (
								<StatusAndTagsSettings
									setToolBarState={setToolBarState}
									type={TagType.CONTACT}
								/>
							)
						},
						{
							key: 'product_fields',
							component: (
								<StatusAndTagsSettings
									setToolBarState={setToolBarState}
									type={TagType.PRODUCT}
								/>
							)
						},
						{
							key: 'promotion_fields',
							component: <PromotionFieldsSettings setToolBarState={setToolBarState} />
						},
						{
							key: 'order_fields',
							component: (
								<StatusAndTagsSettings
									setToolBarState={setToolBarState}
									type={TagType.ORDER}
								/>
							)
						},
						{
							key: 'opportunity_fields',
							component: (
								<StatusAndTagsSettings
									setToolBarState={setToolBarState}
									type={TagType.OPPORTUNITY}
								/>
							)
						},
						{
							key: 'mapping_fields',
							component: (
								<MappingFieldsSettings setToolBarState={setToolBarState} />
							)
						},
						{
							key: 'event_frequency_rules',
							component: <EventFrequencyRules setToolBarState={setToolBarState} />
						}
					]
				},
				{
					name: translateToString('products_settings'),
					restricted: { objectAction: 'ViewCompanySettings' },
					children: [
						{
							key: 'brand_management',
							component: <BrandManagement setToolBarState={setToolBarState} />
						},
						{
							key: 'categories_management',
							component: (
								<CategoryManagement setToolBarState={setToolBarState} />
							)
						},
						{
							key: 'multidimensional_assortments_management',
							component: (
								<MultidimensionalAssortmentsManagement setToolBarState={setToolBarState} />
							)
						}
					]
				},
				{
					name: translateToString('email_template_settings'),
					children: [
						{
							key: 'email_template_list',
							component: (
								<EmailTemplateSettings setToolBarState={setToolBarState} />
							)
						},
						{
							admin: true,
							key: 'emails_v2_list',
							component: (
								<EmailsV2Settings setToolBarState={setToolBarState} />
							)
						}
					]
				},
				{
					name: translateToString('third_party_integrations'),
					children: [
						{
							key: 'calendar_integrations',
							component: (
								<ThirdPartyIntegration
									type="calendar"
									setToolBarState={setToolBarState}
								/>
							)
						}
						// { key: 'emailing_integrations', component: <ThirdPartyIntegration type='emailing' setToolBarState={setToolBarState} /> },
					]
				},
				{
					name: translateToString('orders_settings'),
					admin: true,
					children: [
						{
							key: 'notifications',
							component: (
								<OrderNotificationsSettings setToolBarState={setToolBarState} />
							),
							admin: true
						}
					]
				},
				{
					restricted: { objectAction: 'ViewForm' },
					name: translateToString('forms_settings'),
					children: [
						{
							key: 'form_templates',
							component: (
								<FormTemplateList
									setToolBarState={setToolBarState}
									isAllowedToEditForms={isAllowedToEditForms}
								/>
							)
						},
						...(isAllowedToEditForms
							? [
								{
									key: 'form_views',
									component: (
										<FormViewsList setToolBarState={setToolBarState} />
									)
								}
							]
							: []),
						...(isAllowedToEditForms
							? [
								{
									key: 'metadata',
									component: (
										<MetadataCreator setToolBarState={setToolBarState} />
									)
								}
							]
							: [])
						// { key: 'form_library', component: <CreateForm setToolBarState={setToolBarState} />, admin: true },
					]
				},
				{
					restricted: { objectAction: 'ViewCheckout' },
					admin: true,
					name: translateToString('checkouts'),
					children: [
						{
							key: 'checkouts',
							component: <Checkouts setToolBarState={setToolBarState} />
						}
					]
				},
				// KEEP IMPORT IN LAST
				{
					name: translateToString('import_and_exports'),
					children: [
						{
							key: 'import',
							component: <Import setToolBarState={setToolBarState} />,
							restricted: { objectAction: 'CreateImport' }
						},
						{
							key: 'export',
							component: <Exports />,
							restricted: { objectAction: 'ViewExport' }
						}
					]
				}
			]
		}
	];

	return (
		<SettingsInterpreter
			setToolBarState={setToolBarState}
			categories={categories}
			defaultCategory="my_settings"
		/>
	);
}
